import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, FileText, X, ChevronDown, ChevronLeft, Plus, Loader2, Folder, File } from 'lucide-react';
import { BaseChat, BaseChatConfig } from '@/components/chat/BaseChat';
import { SaveToLibraryButton } from '@/components/chat/SaveToLibraryButton';
import { YAMLFixesDisplay } from '@/components/chat/YAMLFixesDisplay';
import { ChatMessage } from '@/components/chat/chat-utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { apiClient } from '@/api/client';
import { sendWorkflowSuggestionsChatMessage } from '@/api/websocket';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import confluenceIcon from '../assets/icons/confluence.svg';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import gleanIcon from '../assets/icons/glean.svg';
import notionIcon from '../assets/icons/notion-logo-no-background.png';
import notionWhiteIcon from '../assets/icons/notion-logo-white.png';

interface UploadedRunbook {
  id: string;
  name: string;
  enabled: boolean;
}

interface WorkflowSuggestionsChatProps {
  onSuggestionSave?: (suggestion: any) => Promise<boolean | void> | boolean | void;
  onSuggestionCreate?: (suggestion: any, enabledRunbookIds?: string[]) => void;
  onClose?: () => void;
  onRunbookUploaded?: () => void;
  /** Hide the title header bar (ChatGPT-style full-page chat, e.g. Home). */
  hideHeader?: boolean;
  /** Simulated mode: the tenant has no real integrations connected, so the
   *  agent treats every integration as connected and builds sandboxed
   *  workflows the user can explore and test before onboarding. */
  simulated?: boolean;
  /** ChatGPT semantics: open on a blank chat, create the session lazily on
   *  first send (Home page). */
  startBlank?: boolean;
  /** Builder mode: the chat lives beside the workflow builder canvas and
   *  applies generated workflows directly to it instead of offering
   *  Save-to-Library / Create-as-Draft buttons. */
  builderMode?: {
    /** Invoked with the parsed workflow when the agent emits one. */
    onWorkflowGenerated: (workflow: any) => void;
    /** Serialized current canvas, sent with each message so the agent
     *  modifies the existing workflow rather than regenerating. */
    getCurrentWorkflow?: () => any | null;
    /** Simulated builder: generation treats every integration as connected. */
    simulated?: boolean;
    /** Distinct chat-session id (e.g. per workflow being edited). */
    contextId?: string;
  };
}

interface GitHubRepo {
  id: string;
  name: string;
  full_name: string;
}

interface ConfluenceSpace {
  id: string;
  key: string;
  name: string;
}

export function WorkflowSuggestionsChat({
  onSuggestionSave,
  onSuggestionCreate,
  onClose,
  onRunbookUploaded,
  hideHeader,
  simulated,
  startBlank,
  builderMode,
}: WorkflowSuggestionsChatProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [uploadedRunbooks, setUploadedRunbooks] = useState<UploadedRunbook[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);

  // Track which integrations are connected
  const [connectedSources, setConnectedSources] = useState<Set<string>>(new Set());
  const [hasGitHub, setHasGitHub] = useState(false);
  const [hasGitLab, setHasGitLab] = useState(false);

  useEffect(() => {
    // Fetch connected tribal knowledge sources
    apiClient.listTribalKnowledgeSources().then((data: any) => {
      const sources = Array.isArray(data) ? data : data?.sources || [];
      const connected = new Set<string>();
      for (const s of sources) {
        if (s.enabled && s.connection_status === 'connected') {
          connected.add(s.source_type);
        }
      }
      setConnectedSources(connected);
    }).catch(() => {});

    // Check GitHub
    apiClient.getGitHubRepositories().then((r) => setHasGitHub((r?.repositories?.length || 0) > 0)).catch(() => setHasGitHub(false));

    // Check GitLab
    apiClient.getGitLabIntegrationStatus().then((data) => {
      setHasGitLab(data?.is_connected || false);
    }).catch(() => setHasGitLab(false));
  }, []);
  const [attachView, setAttachView] = useState<'main' | 'github' | 'gitlab' | 'confluence' | 'notion' | 'glean'>('main');
  const [githubRepos, setGithubRepos] = useState<GitHubRepo[]>([]);
  const [gitlabRepos, setGitlabRepos] = useState<GitHubRepo[]>([]);
  const [confluenceSpaces, setConfluenceSpaces] = useState<ConfluenceSpace[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingGitlabRepos, setLoadingGitlabRepos] = useState(false);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [githubBrowseRepo, setGithubBrowseRepo] = useState<GitHubRepo | null>(null);
  const [githubBrowsePath, setGithubBrowsePath] = useState('');
  const [githubBrowseFiles, setGithubBrowseFiles] = useState<{ name: string; path: string; type: string }[]>([]);
  const [githubBrowseLoading, setGithubBrowseLoading] = useState(false);
  const [confluenceBrowseSpace, setConfluenceBrowseSpace] = useState<{ id: string; key: string; name: string } | null>(null);
  const [confluencePages, setConfluencePages] = useState<{ id: string; title: string }[]>([]);
  const [confluencePagesLoading, setConfluencePagesLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => Math.min(prev + 10, 90));
    }, 200);

    try {
      const result = await apiClient.uploadRunbook(file);
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadedRunbooks((prev) => [
        ...prev,
        { id: result?.id || `runbook-${Date.now()}`, name: file.name, enabled: true },
      ]);
      onRunbookUploaded?.();
    } catch (err) {
      console.error('Runbook upload failed:', err);
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    }
  }, [onRunbookUploaded]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileUpload(file);
      e.target.value = '';
    },
    [handleFileUpload]
  );

  const toggleRunbook = useCallback((id: string) => {
    setUploadedRunbooks((prev) =>
      prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
    );
  }, []);

  const removeRunbook = useCallback((id: string) => {
    setUploadedRunbooks((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const loadGitHubRepos = useCallback(async () => {
    setLoadingRepos(true);
    try {
      const response = await apiClient.getGitHubRepositories();
      setGithubRepos(
        (response?.repositories || []).map((r) => ({
          id: r.id,
          name: r.repo_name,
          full_name: r.repo_full_name,
        }))
      );
    } catch {
      setGithubRepos([]);
    } finally {
      setLoadingRepos(false);
    }
  }, []);

  const browseGithubDir = useCallback(async (repo: GitHubRepo, path: string) => {
    setGithubBrowseRepo(repo);
    setGithubBrowsePath(path);
    setGithubBrowseLoading(true);
    try {
      const resp = await apiClient.getGitHubRepositoryFiles(repo.id, path || undefined);
      const sorted = (resp.files || []).sort((a, b) => {
        if (a.type === b.type) return a.name.localeCompare(b.name);
        return a.type === 'dir' ? -1 : 1;
      });
      setGithubBrowseFiles(sorted);
    } catch {
      setGithubBrowseFiles([]);
    } finally {
      setGithubBrowseLoading(false);
    }
  }, []);

  const browseConfluenceSpace = useCallback(async (space: { id: string; key: string; name: string }) => {
    setConfluenceBrowseSpace(space);
    setConfluencePagesLoading(true);
    try {
      const pages = await apiClient.getConfluencePages(space.key);
      setConfluencePages(pages || []);
    } catch {
      setConfluencePages([]);
    } finally {
      setConfluencePagesLoading(false);
    }
  }, []);

  const loadGitLabRepos = useCallback(async () => {
    setLoadingGitlabRepos(true);
    try {
      const repos = await (apiClient as any).getGitLabRepositories?.() || [];
      setGitlabRepos(
        (repos || []).map((r: any) => ({
          id: r.id || r.project_id,
          name: r.name || r.project_name,
          full_name: r.full_name || r.path_with_namespace || r.name,
        }))
      );
    } catch {
      setGitlabRepos([]);
    } finally {
      setLoadingGitlabRepos(false);
    }
  }, []);

  const loadConfluenceSpaces = useCallback(async () => {
    setLoadingSpaces(true);
    try {
      const spaces = await apiClient.getConfluenceSpaces();
      setConfluenceSpaces(
        (spaces || []).map((s) => ({ id: s.key, key: s.key, name: s.name }))
      );
    } catch {
      setConfluenceSpaces([]);
    } finally {
      setLoadingSpaces(false);
    }
  }, []);

  const enabledRunbookIds = uploadedRunbooks.filter((r) => r.enabled).map((r) => r.id);
  const sentRunbookIdsRef = useRef<string[]>([]);

  const renderInputPrefix = useCallback(() => (
    <Popover open={attachMenuOpen} onOpenChange={(open) => {
      setAttachMenuOpen(open);
      if (!open) setAttachView('main');
    }}>
      <PopoverTrigger asChild>
        <button
          className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          title="Attach file or source"
        >
          <Plus className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start" side="top">
        {attachView === 'main' && (
          <div className="space-y-1">
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
              onClick={() => {
                fileInputRef.current?.click();
                setAttachMenuOpen(false);
              }}
            >
              <Upload className="h-4 w-4 text-muted-foreground" />
              Upload runbook or script
            </button>
            {(hasGitHub || hasGitLab || connectedSources.has('confluence') || connectedSources.has('notion') || connectedSources.has('glean')) && (
              <>
              <div className="border-t border-border/50 my-1.5" />
              <p className="text-xs font-medium text-muted-foreground px-3 py-1">Source</p>
              </>
            )}
            {hasGitHub && (
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
              onClick={() => {
                setAttachView('github');
                loadGitHubRepos();
              }}
            >
              <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="" className="h-4 w-4" />
              GitHub Repositories
              <ChevronDown className="h-3 w-3 ml-auto opacity-50 -rotate-90" />
            </button>
            )}
            {hasGitLab && (
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
              onClick={() => {
                setAttachView('gitlab');
                loadGitLabRepos();
              }}
            >
              <img src={gitlabIcon} alt="" className="h-4 w-4" />
              GitLab Repositories
              <ChevronDown className="h-3 w-3 ml-auto opacity-50 -rotate-90" />
            </button>
            )}
            {connectedSources.has('confluence') && (
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
              onClick={() => {
                setAttachView('confluence');
                loadConfluenceSpaces();
              }}
            >
              <img src={confluenceIcon} alt="" className="h-4 w-4" />
              Confluence Spaces
              <ChevronDown className="h-3 w-3 ml-auto opacity-50 -rotate-90" />
            </button>
            )}
            {connectedSources.has('notion') && (
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
              onClick={() => setAttachView('notion')}
            >
              <img src={isDark ? notionWhiteIcon : notionIcon} alt="" className="h-4 w-4" />
              Notion Pages
              <ChevronDown className="h-3 w-3 ml-auto opacity-50 -rotate-90" />
            </button>
            )}
            {connectedSources.has('glean') && (
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
              onClick={() => setAttachView('glean')}
            >
              <img src={gleanIcon} alt="" className="h-4 w-4" />
              Glean Documents
              <ChevronDown className="h-3 w-3 ml-auto opacity-50 -rotate-90" />
            </button>
            )}
          </div>
        )}
        {attachView === 'github' && (
          <div className="space-y-1">
            <button
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => {
                if (githubBrowseRepo && githubBrowsePath) {
                  const parent = githubBrowsePath.includes('/') ? githubBrowsePath.slice(0, githubBrowsePath.lastIndexOf('/')) : '';
                  browseGithubDir(githubBrowseRepo, parent);
                } else if (githubBrowseRepo) {
                  setGithubBrowseRepo(null);
                  setGithubBrowseFiles([]);
                  setGithubBrowsePath('');
                } else {
                  setAttachView('main');
                }
              }}
            >
              <ChevronLeft className="h-3 w-3" />
              Back
            </button>
            {!githubBrowseRepo ? (
              <>
                <p className="text-xs font-medium text-muted-foreground px-3 py-1">Connected Repositories</p>
                {loadingRepos ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : githubRepos.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2">No connected repositories</p>
                ) : (
                  githubRepos.map((repo) => (
                    <button
                      key={repo.id}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
                      onClick={() => browseGithubDir(repo, '')}
                    >
                      <Folder className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{repo.full_name || repo.name}</span>
                    </button>
                  ))
                )}
              </>
            ) : (
              <>
                <p className="text-xs font-medium text-muted-foreground px-3 py-1 truncate">
                  {githubBrowseRepo.full_name}{githubBrowsePath ? `/${githubBrowsePath}` : ''}
                </p>
                {githubBrowseLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : githubBrowseFiles.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2">Empty directory</p>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto">
                    {githubBrowseFiles.map((item) => (
                      <button
                        key={item.path}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
                        onClick={() => {
                          if (item.type === 'dir') {
                            browseGithubDir(githubBrowseRepo, item.path);
                          } else {
                            setUploadedRunbooks((prev) => [
                              ...prev,
                              { id: `github-file:${githubBrowseRepo.full_name}:${item.path}`, name: `${githubBrowseRepo.full_name}/${item.path}`, enabled: true },
                            ]);
                            setAttachMenuOpen(false);
                            setAttachView('main');
                            setGithubBrowseRepo(null);
                            setGithubBrowseFiles([]);
                            setGithubBrowsePath('');
                          }
                        }}
                      >
                        {item.type === 'dir' ? (
                          <Folder className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        ) : (
                          <File className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                        <span className="truncate">{item.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {attachView === 'gitlab' && (
          <div className="space-y-1">
            <button
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setAttachView('main')}
            >
              <ChevronLeft className="h-3 w-3" />
              Back
            </button>
            <p className="text-xs font-medium text-muted-foreground px-3 py-1">Connected Repositories</p>
            {loadingGitlabRepos ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : gitlabRepos.length === 0 ? (
              <p className="text-xs text-muted-foreground px-3 py-2">No connected repositories</p>
            ) : (
              gitlabRepos.map((repo) => (
                <button
                  key={repo.id}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
                  onClick={() => {
                    setUploadedRunbooks((prev) => [
                      ...prev,
                      { id: `gitlab-${repo.id}`, name: repo.full_name || repo.name, enabled: true },
                    ]);
                    setAttachMenuOpen(false);
                    setAttachView('main');
                  }}
                >
                  <Folder className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{repo.full_name || repo.name}</span>
                </button>
              ))
            )}
          </div>
        )}
        {attachView === 'confluence' && (
          <div className="space-y-1">
            <button
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => {
                if (confluenceBrowseSpace) {
                  setConfluenceBrowseSpace(null);
                  setConfluencePages([]);
                } else {
                  setAttachView('main');
                }
              }}
            >
              <ChevronLeft className="h-3 w-3" />
              Back
            </button>
            {!confluenceBrowseSpace ? (
              <>
                <p className="text-xs font-medium text-muted-foreground px-3 py-1">Available Spaces</p>
                {loadingSpaces ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : confluenceSpaces.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2">No connected spaces</p>
                ) : (
                  confluenceSpaces.map((space) => (
                    <button
                      key={space.id}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
                      onClick={() => browseConfluenceSpace(space)}
                    >
                      <Folder className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{space.name}</span>
                    </button>
                  ))
                )}
              </>
            ) : (
              <>
                <p className="text-xs font-medium text-muted-foreground px-3 py-1 truncate">
                  {confluenceBrowseSpace.name}
                </p>
                {confluencePagesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : confluencePages.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2">No pages found</p>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto">
                    {confluencePages.map((page) => (
                      <button
                        key={page.id}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left"
                        onClick={() => {
                          setUploadedRunbooks((prev) => [
                            ...prev,
                            { id: `confluence-page:${confluenceBrowseSpace.key}:${page.id}`, name: `${confluenceBrowseSpace.name} / ${page.title}`, enabled: true },
                          ]);
                          setAttachMenuOpen(false);
                          setAttachView('main');
                          setConfluenceBrowseSpace(null);
                          setConfluencePages([]);
                        }}
                      >
                        <File className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{page.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {attachView === 'notion' && (
          <div className="space-y-1">
            <button
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setAttachView('main')}
            >
              <ChevronLeft className="h-3 w-3" />
              Back
            </button>
            <p className="text-xs font-medium text-muted-foreground px-3 py-1">Notion Pages</p>
            <div className="px-2 pb-1">
              <input
                type="text"
                placeholder="Search pages by title..."
                className="w-full h-7 px-2 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                    const query = (e.target as HTMLInputElement).value.trim();
                    setUploadedRunbooks((prev) => [
                      ...prev,
                      { id: `notion-${Date.now()}`, name: `Notion: ${query}`, enabled: true },
                    ]);
                    setAttachMenuOpen(false);
                    setAttachView('main');
                  }
                }}
              />
              <p className="text-[10px] text-muted-foreground mt-1">Press Enter to add as context source</p>
            </div>
          </div>
        )}
        {attachView === 'glean' && (
          <div className="space-y-1">
            <button
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setAttachView('main')}
            >
              <ChevronLeft className="h-3 w-3" />
              Back
            </button>
            <p className="text-xs font-medium text-muted-foreground px-3 py-1">Glean Documents</p>
            <div className="px-2 pb-1">
              <input
                type="text"
                placeholder="Search documents..."
                className="w-full h-7 px-2 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                    const query = (e.target as HTMLInputElement).value.trim();
                    setUploadedRunbooks((prev) => [
                      ...prev,
                      { id: `glean-${Date.now()}`, name: `Glean: ${query}`, enabled: true },
                    ]);
                    setAttachMenuOpen(false);
                    setAttachView('main');
                  }
                }}
              />
              <p className="text-[10px] text-muted-foreground mt-1">Press Enter to add as context source</p>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  ), [attachMenuOpen, attachView, githubRepos, gitlabRepos, confluenceSpaces, loadingRepos, loadingGitlabRepos, loadingSpaces, loadGitHubRepos, loadGitLabRepos, loadConfluenceSpaces, isDark, githubBrowseRepo, githubBrowsePath, githubBrowseFiles, githubBrowseLoading, browseGithubDir, confluenceBrowseSpace, confluencePages, confluencePagesLoading, browseConfluenceSpace]);

  const config: BaseChatConfig = {
    sendMessage: (inputText: string) => {
      // Track sent runbook IDs locally
      if (enabledRunbookIds.length > 0) {
        sentRunbookIdsRef.current = [...sentRunbookIdsRef.current, ...enabledRunbookIds];
      }
      // Always send ALL accumulated runbook IDs (not just current message's) so the router
      // has them available when the workflow is eventually generated on a later message
      const allRunbookIds = sentRunbookIdsRef.current.length > 0 ? sentRunbookIdsRef.current : enabledRunbookIds;
      const msgId = sendWorkflowSuggestionsChatMessage(
        inputText,
        allRunbookIds.length > 0 ? allRunbookIds : undefined,
        undefined,
        (builderMode || simulated)
          ? {
              currentWorkflow: builderMode?.getCurrentWorkflow?.() || undefined,
              simulated: simulated || builderMode?.simulated,
            }
          : undefined,
      );
      // Clear uploaded runbooks after sending (they were included with this message)
      if (uploadedRunbooks.length > 0) {
        setUploadedRunbooks([]);
      }
      return msgId;
    },
    samplePrompts: builderMode
      ? [
          'When a pod is crash-looping in production, investigate and post a summary to Slack',
          'Open a Terraform PR when someone requests new cloud infrastructure',
          'Add an approval step before any remediation runs',
          'What can I automate with my connected integrations?',
        ]
      : [
          'Analyze my uploaded runbook and suggest automations',
          'What can I automate for Kubernetes pod failures?',
          'Suggest a workflow for deployment rollback on error',
          'Help me automate our on-call escalation process',
        ],
    headerIcon: undefined,
    headerTitle: builderMode ? 'Workflow Agent' : 'Workflow Assistant',
    hideHeader,
    emptyHeading: builderMode ? 'What should this workflow do?' : 'What would you like to automate?',
    emptyDescription: builderMode
      ? 'Describe the automation in plain English — I\'ll ask for anything I need, then build it right on the canvas. You can keep chatting to refine it.'
      : 'Upload a runbook or script, describe your ops process, or ask me to suggest workflows based on your integrations.',
    inputPlaceholder: 'Describe what you want to automate...',
    colorTheme: 'primary',
    fixType: 'yaml',
    contextType: 'workflow_suggestions',
    contextId: builderMode?.contextId || 'workflow-suggestions',
    hideEmptyIcon: true,
    hideDisclaimer: true,
    requiredProduct: 'workflows',
    getAttachments: () => {
      const atts: Array<{ type: string; name: string }> = [];
      for (const rb of uploadedRunbooks) {
        if (rb.enabled) atts.push({ type: 'file', name: rb.name });
      }
      return atts.length > 0 ? atts : [];
    },
    onWorkflowSave: builderMode ? undefined : onSuggestionSave,
    onWorkflowCreate: !builderMode && onSuggestionCreate
      ? (workflow: any) => {
          // Primary: read source_runbook_ids embedded by the router in the workflow JSON
          // Fallback: use locally tracked IDs from this session
          const runbookIds = workflow.source_runbook_ids || sentRunbookIdsRef.current || [];
          onSuggestionCreate(workflow, runbookIds.length > 0 ? runbookIds : enabledRunbookIds);
        }
      : undefined,
    onWorkflowGenerated: builderMode?.onWorkflowGenerated,
    compactWorkflowBlocks: !!builderMode,
    // One chat session per workflow: the builder keeps the workflow's
    // creation history in a single conversation, so no history/new-chat.
    hideSessionControls: !!builderMode,
    simulatedBadge: simulated || builderMode?.simulated,
    startBlankSession: startBlank,
    renderInputPrefix,
    renderHeaderExtra: onClose ? () => (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onClose}
        title="Hide chat"
      >
        <X className="h-4 w-4" />
      </Button>
    ) : undefined,
    renderInputAbove: uploadedRunbooks.length > 0 ? () => (
      <div className="flex items-center gap-1.5 flex-wrap px-1">
        {uploadedRunbooks.map((runbook) => (
          <Badge
            key={runbook.id}
            variant={runbook.enabled ? 'default' : 'outline'}
            className={cn(
              'gap-1 cursor-pointer select-none pr-1 text-[11px] h-5',
              runbook.enabled ? '' : 'opacity-60'
            )}
            onClick={() => toggleRunbook(runbook.id)}
          >
            <FileText className="h-2.5 w-2.5" />
            <span className="max-w-[180px] truncate">{runbook.name}</span>
            <button
              className="ml-0.5 rounded-full p-0.5 hover:bg-background/20"
              onClick={(e) => { e.stopPropagation(); removeRunbook(runbook.id); }}
            >
              <X className="h-2 w-2" />
            </button>
          </Badge>
        ))}
      </div>
    ) : undefined,
    renderFixes: (message: ChatMessage) => {
      if (!message.yamlFixes) return null;
      return (
        <div className="mt-3 space-y-2">
          <YAMLFixesDisplay yamlFixes={message.yamlFixes} />
          <div className="flex gap-2 pt-1">
            <SaveToLibraryButton
              onSave={() => onSuggestionSave?.(message.yamlFixes)}
              className="text-xs h-7 px-3 rounded-md border border-border bg-background hover:bg-muted transition-colors"
            />
            <Button
              size="sm"
              className="text-xs h-7"
              onClick={() => onSuggestionCreate?.(message.yamlFixes)}
            >
              Create as Draft
            </Button>
          </div>
        </div>
      );
    },
    renderStreamingFixes: (fixes) => <YAMLFixesDisplay yamlFixes={fixes as any} />,
    fixesLoadingText: undefined,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".md,.txt,.pdf,.doc,.docx,.sh,.py,.yaml,.yml,.json"
        onChange={handleFileSelect}
      />

      {/* Chat */}
      <div className="flex-1 min-h-0">
        <BaseChat config={config} />
      </div>
    </div>
  );
}
